import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn, gridExportMatrix } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNavActions } from '../../hooks/useNavActions';
import { FormTitle } from '../../components/FormTitle';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { StepHint } from '../../components/StepHint/StepHint';
import { useConfirm } from '../../components/confirm';
import { useNotify } from '../../components/notifications/useNotify';
import { runLoading, LoaderSymbol, useDelayedFlag } from '../../components/loading';
import { fmtLocalDateTime, fmtLocalDate } from '../../utils/datetime';
// Project Notes — the SHARED ALPACA-parity popup (standing rule 8: consume the
// shared capability, never reinvent). GoLive's ProjectNotesViewController targets
// Samplecheckin_DetailView_FieldDataEntry, so this form gets the same button; its
// SetCriteria branch for "FieldDataEntry" scopes notes to the JOB.
import { NotesListPopup, useNotesCount } from '../analysis/SdmsProjectNotes';
// Trend popup (Routing the Trend Icon, Part B) — the shared site-trend chart the
// Monitoring "Trend" column opens.
import { TrendChartPopup, TrendIconButton } from '../trend';
// Shared Field-Data column-config engine — turns the job's Sample Field
// Configuration into grid columns (Entry / Validation / Tracking all use it).
import {
  type FieldConfigItem, type DynamicFieldSet, EMPTY_DYN,
  assembleTab, applyFieldConfig, mergeDynamic,
  STATION_FIELD_MAP, SAMPLING_FIELD_MAP, MONITORING_FIELD_MAP,
} from './fieldDataColumns';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface FieldDataEntryRow {
  jobId:             string;
  jobOid:            string;
  client:            string;
  projectId:         string;
  projectName:       string;
  sampleMatrix:      string;
  status:            string;
  statusId:          string;
  samplingStatusInt: number;
  testCount:         number;
  receivedDate:      string | null;
  dueDate:           string | null;
  updatedDate:       string | null;
}

interface StationRow {
  sampleLoginOid:  string;
  stationId:       string;
  stationName:     string;
  blended:         boolean;
  latitude:        string | null;
  longitude:       string | null;
  temperature:     string | null;
  humidity:        string | null;
  stationComment:  string | null;
  [key: string]: unknown; // dynamic (config-driven) extra fields
}

interface SamplingRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  sampleType:     string;
  sampleId:       string;
  collectorOid:   string | null;
  collectorName:  string | null;
  collectedDate:  string | null;
  sampleComment:  string | null;
  testSummary:    string | null;
  enteredBy:      string | null;
  enteredByOid?:  string | null; // set only when freshly stamped this session
  enteredDate:    string | null;
  isHold:         boolean;
  [key: string]: unknown; // dynamic (config-driven) extra fields
}

interface UserItem {
  userOid:  string;
  userName: string;
}

interface MonitoringRow {
  sampleParameterOid: string;
  sampleLoginOid:     string;
  sampleId:           string;
  test:               string;
  methodName:         string;
  parameter:          string;
  result:             string | null;
  unit:               string;
  monitoredBy:        string | null;
  monitoredByOid?:    string | null; // set when the user picks Monitored By
  monitoredDate:      string | null;
  enteredBy:          string | null;
  enteredByOid?:      string | null; // set only when freshly stamped this session
  enteredDate:        string | null;
  isHold:             boolean;
  [key: string]: unknown; // dynamic (config-driven) extra fields
}

type TabKey = 'stations' | 'sampling' | 'monitoring';

// ── List-first flow (ALPACA / prototype parity) ──────────────────────────────
// The page lands on a grid of jobs pending field-data capture (SamplingStatus=0,
// PendingCompletion / FD-2-1) — JobID · Client · ProjectID · ProjectName ·
// SampleMatrix · Status · StatusID. Tick a Job ID and click Load (the legacy
// "Retrieve") to open that ONE job in the three editable tabs. List → (select) →
// detail is the only flow (standing rule 10), mirroring Field Data Validation.

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function statusPillVariant(statusId: string): string {
  if (statusId === 'FD-2-1') return 'warn';
  return 'info';
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function FieldDataEntryPage() {
  // ── List state (front view — jobs pending field-data capture) ───────────────
  const [view,        setView]        = useState<'list' | 'detail'>('list');
  const [listRows,    setListRows]    = useState<FieldDataEntryRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listLoadMs,  setListLoadMs]  = useState<number | null>(null);
  const [listError,   setListError]   = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [listVisible, setListVisible] = useState<FieldDataEntryRow[]>([]);
  const [search,      setSearch]      = useState('');

  // ── Selected job (one job, opened from the list via Load) ───────────────────
  const [selectedJob,    setSelectedJob]    = useState<FieldDataEntryRow | null>(null);
  const [activeTab,      setActiveTab]      = useState<TabKey>('stations');
  const [detailLoadMs,   setDetailLoadMs]   = useState<number | null>(null);
  const detailT0 = useRef<number>(0);

  // ── Project Notes (GoLive ProjectNotesViewController, job scope) ────────────
  // The route lives on THIS module's controller and delegates to the shared
  // INoteReader, so the popup, the dbo.Notes storage and the author-only rule
  // (§14a Rule C) are all the existing shared ones. Hook is called
  // unconditionally (rules of hooks); with no job (or the combined sentinel) the
  // URL is undefined → count 0.
  const [notesOpen, setNotesOpen] = useState(false);

  // ── Detail data ────────────────────────────────────────────────────────────
  const [stations,     setStations]     = useState<StationRow[]>([]);
  const [sampling,     setSampling]     = useState<SamplingRow[]>([]);
  const [monitoring,   setMonitoring]   = useState<MonitoringRow[]>([]);

  // Project Notes are PER-JOB. In the combined view they apply only when the loaded
  // data is a SINGLE job (the filter narrowed to one Job ID) — then the button is
  // shown, scoped to that job. With multiple jobs loaded (no single note context)
  // or nothing loaded, the button is hidden.
  // Project Notes are PER-JOB — scoped to the one job opened from the list.
  const notesJobOid   = selectedJob?.jobOid;
  const notesJobLabel = selectedJob?.jobId ?? '';
  const notesUrl = notesJobOid
    ? `/api/v1/field-data-entry/${notesJobOid}/project-notes`
    : undefined;
  const notesCount = useNotesCount('', null, notesUrl);
  // Trend popup (Routing the Trend Icon, Part B) — the Monitoring "Trend" column.
  const [trendOpen,  setTrendOpen]  = useState(false);
  const [trendSite,  setTrendSite]  = useState('');
  const [trendParam, setTrendParam] = useState('');
  // Each tab grid's OWN filtered+sorted rows — the tab's Export must reflect
  // exactly what's shown (column filters + sort), not the full set.
  const [stationVisible,    setStationVisible]    = useState<StationRow[]>([]);
  const [samplingVisible,   setSamplingVisible]   = useState<SamplingRow[]>([]);
  const [monitoringVisible, setMonitoringVisible] = useState<MonitoringRow[]>([]);
  const [fieldConfig,  setFieldConfig]  = useState<FieldConfigItem[]>([]);
  const [stationDyn,   setStationDyn]   = useState<DynamicFieldSet>(EMPTY_DYN);
  const [samplingDyn,  setSamplingDyn]  = useState<DynamicFieldSet>(EMPTY_DYN);
  const [monitoringDyn, setMonitoringDyn] = useState<DynamicFieldSet>(EMPTY_DYN);
  const [detailLoading, setDetailLoading] = useState(false);
  // Phase-2 (dynamic, matrix-config columns) still resolving — drives the small
  // "Loading columns…" hint so the late-arriving extra columns are expected, not
  // a glitch (the base grid is already shown; this never blocks it).
  const [dynLoading, setDynLoading] = useState(false);
  // Only surface the hint when Phase 2 actually runs long (>200 ms) — avoids a
  // flash on fast loads (shared flicker-delay pattern, §18.6).
  const showColsLoading = useDelayedFlag(dynLoading, 200);

  // ── User list for CollectedBy combo ────────────────────────────────────────
  const [users, setUsers] = useState<UserItem[]>([]);
  // Global user/employee roster for the EnteredBy combo. GoLive shows the
  // Employee DisplayName (e.g. "Service", "C16294", "E105118"). Stored value is
  // the Employee Oid (EnteredBy FK).
  const [employees, setEmployees] = useState<{ oid: string; name: string }[]>([]);

  // ── Sampling bulk-apply state ───────────────────────────────────────────────
  const [selectedSamplingIds, setSelectedSamplingIds] = useState<Set<string | number>>(new Set());
  const [bulkCollectorOid, setBulkCollectorOid] = useState('');
  const [bulkDate,         setBulkDate]         = useState('');
  const [bulkApplying] = [false]; // kept as const false — apply is now instant (local state only)

  // ── Monitoring bulk-apply state
  const [selectedMonitoringIds, setSelectedMonitoringIds] = useState<Set<string | number>>(new Set());
  const [bulkMonitoredByOid,    setBulkMonitoredByOid]    = useState('');
  const [bulkMonitoredDate,     setBulkMonitoredDate]     = useState('');
  const [bulkMonitoringApplying] = [false]; // kept as const false — apply is now instant (local state only)
  const [isDirty,     setIsDirty]     = useState(false);
  const [activeStep,  setActiveStep]  = useState<string>('job'); // tracks stepper position
  // Permission gate ported from GoLive FieldDataEntryViewController.OnActivated —
  // Save/Submit are enabled only for admins or roles with Write=true on the
  // FieldDataEntry navigation permission.
  const [canWrite, setCanWrite] = useState(true);

  // Module Access Control (Role-Based MAC) — layered ON TOP of the nav-permission
  // canWrite above, same pattern as Sampling Allocation (useNavActions). Falls open
  // for admins / no configured MAC level, so it only ever TIGHTENS. Effective:
  //   write  = canWrite (nav Write) AND perm.write (MAC)
  //   delete = canWrite            AND perm.delete (MAC)
  const perm = useNavActions('fielddata-entry');
  const canSave   = canWrite && perm.write;
  const canDelete = canWrite && perm.delete;

  // ── Station selection state (for Select All highlight only)
  const [selectedStationIds,    setSelectedStationIds]    = useState<Set<string | number>>(new Set());

  // ── Field-data cell context menu (legacy FieldDataEntryViewController parity:
  // GridView_FillContextMenuItems wires Refresh + Copy To All Cell to ALL three
  // field-data grids). ONE column-aware menu for Station / Sampling / Monitoring:
  // `grid` selects which rows/selection apply, `field` is the right-clicked
  // column so Copy fills that column, `rowId` is the source row's id. ───────────
  type FdGrid = 'station' | 'sampling' | 'monitoring';
  const [cellMenu, setCellMenu] = useState<{ visible: boolean; x: number; y: number; grid: FdGrid; rowId: string | number | null; field: string | null }>
    ({ visible: false, x: 0, y: 0, grid: 'station', rowId: null, field: null });

  const closeCellMenu = useCallback(() => setCellMenu(m => ({ ...m, visible: false })), []);
  const openCellMenu = useCallback((grid: FdGrid, x: number, y: number, rowId: string | number, field: string) =>
    setCellMenu({ visible: true, x, y, grid, rowId, field }), []);

  const currentUser = useAuthStore(s => s.user);

  const notify = useNotify();
  const { confirm: confirmDlg } = useConfirm();

  // ── Load list (jobs pending field-data capture) ─────────────────────────────
  const loadList = useCallback(async (searchText?: string) => {
    setListLoading(true);
    setListError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (searchText) params.search = searchText;
      const res = await api.get('/api/v1/field-data-entry', { params });
      setListRows((res.data as { data: FieldDataEntryRow[] }).data ?? []);
      setSelectedIds(new Set());
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setListLoadMs(performance.now() - t0);
      setListLoading(false);
    }
  }, []);

  useEffect(() => { void loadList(); }, [loadList]);

  // Single-select (radio-style) — only one job may be ticked at a time, since Load
  // opens one record. Header "select all" collapses to the last row. (Field Data
  // Validation parity.)
  const handleSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const newlyAdded = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(new Set([newlyAdded ?? [...next][next.size - 1]!]));
  }, [selectedIds]);

  // CollectedBy roster — loaded PER JOB, not once on mount. dbo.Collector is a
  // per-client roster (one row per person PER client), so the API scopes it to the
  // selected job's client; fetching it job-less returned every client's rows and the
  // combo listed the same person once per client (user report 2026-08-03).
  useEffect(() => {
    if (!selectedJob) { setUsers([]); return; }
    api.get(`/api/v1/field-data-entry/users?jobOid=${encodeURIComponent(selectedJob.jobOid)}`)
      .then(r => setUsers((r.data as { data: UserItem[] }).data ?? []))
      .catch(() => { /* non-fatal — the combo simply stays empty */ });
  }, [selectedJob]);

  // Global user roster for the EnteredBy combo (login UserName + Oid).
  useEffect(() => {
    api.get('/api/v1/lookups/employees')
      .then(r => setEmployees(((r.data as { data: { oid: string; displayName: string; userName: string }[] }).data ?? [])
        .map(e => ({ oid: e.oid, name: e.displayName || e.userName }))))
      .catch(() => { /* non-fatal — the combo simply stays empty */ });
  }, []);

  // ── Permission gate (ported from GoLive FieldDataEntryViewController.OnActivated) ──
  useEffect(() => {
    api.get('/api/v1/field-data-entry/permissions')
      .then(res => setCanWrite(Boolean((res.data as { data: { canWrite: boolean } }).data?.canWrite)))
      .catch(() => setCanWrite(false));
  }, []);

  // ── Load detail tabs ────────────────────────────────────────────────────────
  const loadDetail = useCallback(async (job: FieldDataEntryRow) => {
    detailT0.current = performance.now();
    setDetailLoading(true);
    const base = `/api/v1/field-data-entry/${job.jobOid}`;
    // ── Phase 1 · BASE rows first ──────────────────────────────────────────────
    // Paint the grid (StationLocation / SiteName, SampleID, Result …) as soon as
    // the core rows return — do NOT block the whole detail on the heavier
    // dynamic-field introspection calls (user report 2026-09-01: "SiteName must
    // show on first load"). The base rows carry every fixed column; the raw rows
    // render immediately (EMPTY_DYN merge = no-op), and the config-driven extra
    // columns fill in in phase 2 without holding up the first paint.
    let stationRows: StationRow[] = [];
    let samplingRows: SamplingRow[] = [];
    let monitoringRows: MonitoringRow[] = [];
    try {
      const [s, sp, m, fc] = await Promise.all([
        api.get(`${base}/stations`),
        api.get(`${base}/sampling`),
        api.get(`${base}/monitoring`),
        api.get(`${base}/field-config`),
      ]);
      stationRows    = (s.data  as { data: StationRow[] }).data ?? [];
      samplingRows   = (sp.data as { data: SamplingRow[] }).data ?? [];
      monitoringRows = (m.data  as { data: MonitoringRow[] }).data ?? [];
      setStations(stationRows);
      setSampling(samplingRows);
      setMonitoring(monitoringRows);
      setFieldConfig((fc.data as { data: FieldConfigItem[] }).data ?? []);
      setDetailLoadMs(performance.now() - detailT0.current);
      // (Re)loading the detail clears any ticked rows on all three tabs — a tab
      // Refresh (or reopening a job) must not leave a stale checkbox checked
      // (user report 2026-09-04; the Station/Sampling/Monitoring grids bind to
      // these Sets and loadDetail reloads all three tabs' rows).
      setSelectedStationIds(new Set());
      setSelectedSamplingIds(new Set());
      setSelectedMonitoringIds(new Set());
    } catch {
      notify.error('Failed to load job details.');
      setDetailLoading(false);
      setDynLoading(false);
      return;
    } finally {
      // The grid is usable now — stop the blocking spinner even while the
      // dynamic columns are still resolving.
      setDetailLoading(false);
    }

    // ── Phase 2 · dynamic (config-driven) extra columns ────────────────────────
    // Non-blocking: the base grid is already shown. If this fails the fixed
    // columns still work, so it never leaves the grid stuck loading. The small
    // "Loading columns…" hint (dynLoading) signals the extra columns are coming.
    setDynLoading(true);
    try {
      const [dStation, dSampling, dMonitoring] = await Promise.all([
        api.get(`${base}/dynamic-fields?fieldClass=1`),
        api.get(`${base}/dynamic-fields?fieldClass=0`),
        api.get(`${base}/dynamic-fields?fieldClass=2`),
      ]);
      const stationDynSet    = (dStation.data as { data: DynamicFieldSet }).data ?? EMPTY_DYN;
      const samplingDynSet   = (dSampling.data as { data: DynamicFieldSet }).data ?? EMPTY_DYN;
      const monitoringDynSet = (dMonitoring.data as { data: DynamicFieldSet }).data ?? EMPTY_DYN;
      setStationDyn(stationDynSet);
      setSamplingDyn(samplingDynSet);
      setMonitoringDyn(monitoringDynSet);
      // Merge dynamic values onto each row so they edit as normal grid cells.
      setStations(mergeDynamic(stationRows, stationDynSet, r => r.sampleLoginOid));
      setSampling(mergeDynamic(samplingRows, samplingDynSet, r => r.sampleLoginOid));
      setMonitoring(mergeDynamic(monitoringRows, monitoringDynSet, r => r.sampleParameterOid));
    } catch {
      /* non-fatal — the fixed columns already render; dynamic extras stay empty */
    } finally {
      setDynLoading(false);
    }
  }, [notify]);

  // ── Open a job from the list (Load / double-click) ─────────────────────────
  // Opens ONE job in the three editable tabs — the legacy "Retrieve" flow.
  const openDetail = useCallback((job: FieldDataEntryRow) => {
    setSelectedJob(job);
    setView('detail');
    setActiveTab('stations');
    setActiveStep('stations');
    setIsDirty(false);
    setStations([]); setSampling([]); setMonitoring([]);
    setFieldConfig([]); setStationDyn(EMPTY_DYN); setSamplingDyn(EMPTY_DYN); setMonitoringDyn(EMPTY_DYN);
    setSelectedStationIds(new Set());
    setSelectedSamplingIds(new Set());
    setSelectedMonitoringIds(new Set());
    // Clear the Sampling + Monitoring bulk-apply inputs (CollectedBy / CollectedDate,
    // MonitoredBy / MonitoredDate) so a previously-opened job's picks never carry into
    // the next one (user report 2026-09-28: stale values shown after Back → new job).
    setBulkCollectorOid(''); setBulkDate('');
    setBulkMonitoredByOid(''); setBulkMonitoredDate('');
    void loadDetail(job);
  }, [loadDetail]);

  // Return to the list view and refresh it (a saved/submitted job may have moved).
  const closeDetail = useCallback(() => {
    setView('list');
    setSelectedJob(null);
    setActiveStep('job');
    setIsDirty(false);
    setStations([]); setSampling([]); setMonitoring([]);
    setFieldConfig([]);
    setStationDyn(EMPTY_DYN);
    setSamplingDyn(EMPTY_DYN);
    setMonitoringDyn(EMPTY_DYN);
    // Clear the bulk-apply inputs on the way out too (see openDetail).
    setBulkCollectorOid(''); setBulkDate('');
    setBulkMonitoredByOid(''); setBulkMonitoredDate('');
    void loadList();
  }, [loadList]);

  // ── Save (confirm → single batch SP → "Saved successfully.") ──────────────
  // All edits are local-state-only until this fires. One HTTP call replaces N.
  const handleSave = useCallback(async () => {
    if (!selectedJob) return;
    if (!canSave) {
      notify.error('You do not have permission to save field data.');
      return;
    }
    const ok = await confirmDlg({ message: 'Do you want to Save?', tone: 'default' });
    if (!ok) return;
    try {
      const jobOid = selectedJob.jobOid;
      // Collect editable dynamic (config-driven) cell values to persist back to
      // their real SampleLogIn / SampleParameter columns.
      const dynChanges = (rows: Array<Record<string, unknown>>, dyn: DynamicFieldSet, keyOf: (r: Record<string, unknown>) => string) =>
        rows.flatMap(r => dyn.columns.filter(c => !c.isReadonly).map(c => ({
          rowOid: keyOf(r),
          fieldId: c.fieldId,
          value: r[c.fieldId] == null ? null : String(r[c.fieldId]),
        })));
      await runLoading(
        (async () => {
          await api.post('/api/v1/field-data-entry/save-batch', {
            samplingRows: sampling.map(r => ({
              sampleLoginOid: r.sampleLoginOid,
              collectorOid:   r.collectorOid   ?? null,
              collectedDate:  r.collectedDate  ?? null,
              sampleComment:  r.sampleComment  ?? null,
              enteredByOid:   r.enteredByOid ?? null,
              enteredDate:    r.enteredDate  ?? null,
            })),
            monitoringRows: monitoring.map(r => ({
              sampleParameterOid: r.sampleParameterOid,
              sampleLoginOid:     r.sampleLoginOid,
              result:             r.result ?? null,
              // Monitored By/Date are the Monitoring tab's own columns — saved only
              // when the user actually picks them (never from Collected). The name is
              // resolved to an Employee Oid server-side (MonitoredBy FK -> Employee).
              monitoredByName:    r.monitoredBy ?? null,
              monitoredDate:      r.monitoredDate ?? null,
              enteredByOid:       r.enteredByOid ?? null,
              enteredDate:        r.enteredDate  ?? null,
            })),
            stationRows: stations.map(r => ({
              sampleLoginOid: r.sampleLoginOid,
              blended:        r.blended,
              latitude:       r.latitude       ?? null,
              longitude:      r.longitude      ?? null,
              temperature:    r.temperature    ?? null,
              humidity:       r.humidity       ?? null,
              stationComment: r.stationComment ?? null,
            })),
          });
          const dynPuts: Array<Promise<unknown>> = [];
          const sCh = dynChanges(stations, stationDyn, r => String(r.sampleLoginOid));
          const spCh = dynChanges(sampling, samplingDyn, r => String(r.sampleLoginOid));
          const mCh = dynChanges(monitoring, monitoringDyn, r => String(r.sampleParameterOid));
          if (sCh.length)  dynPuts.push(api.put(`/api/v1/field-data-entry/${jobOid}/dynamic-fields`, { fieldClass: 1, changes: sCh }));
          if (spCh.length) dynPuts.push(api.put(`/api/v1/field-data-entry/${jobOid}/dynamic-fields`, { fieldClass: 0, changes: spCh }));
          if (mCh.length)  dynPuts.push(api.put(`/api/v1/field-data-entry/${jobOid}/dynamic-fields`, { fieldClass: 2, changes: mCh }));
          if (dynPuts.length) await Promise.all(dynPuts);
        })(),
        { blocking: true, label: 'Saving…' }
      );
      setIsDirty(false);
      setActiveStep('save');
      // Saving stamps SampleLogIn.SamplingSaved=1, so the job moves from
      // "Pending Data Entry" (FD-2-1) to "Pending Submission" (FD-2-2) — reflect
      // that in the header/General Information immediately (the list SP derives
      // the same on next load). GoLive FieldDataEntrySave parity.
      setSelectedJob(j => (j && j.statusId === 'FD-2-1')
        ? { ...j, status: 'Pending Submission', statusId: 'FD-2-2' } : j);
      notify.success('Saved successfully.');
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw
        : (raw as { message?: string } | undefined)?.message ?? 'Save failed. Please try again.';
      notify.error(msg);
    }
  }, [selectedJob, canSave, sampling, monitoring, stations, stationDyn, samplingDyn, monitoringDyn, confirmDlg, notify]);

  // ── Submit ──────────────────────────────────────────────────────────────────
  // Validation order (mirrors GoLive FieldDataEntryComplete_Execute):
  //  1. Must have no unsaved changes
  //  2. At least one Sampling row selected
  //  3. All monitoring parameters for each selected sample must be selected
  //  4. Mandatory fields on selected Sampling rows: CollectedBy + CollectedDate
  //  5. Mandatory fields on selected Monitoring rows: Result + MonitoredBy + MonitoredDate
  const handleSubmit = useCallback(async () => {
    if (!selectedJob) return;
    if (!canSave) {
      notify.error('You do not have permission to submit field data.');
      return;
    }

    const errors: string[] = [];

    // 1. Unsaved changes block submit
    if (isDirty) {
      errors.push('There are unsaved changes — please Save before submitting.');
    }

    // 2. At least one sampling row selected
    if (selectedSamplingIds.size === 0) {
      errors.push('Select at least one Sample ID in the Sampling Information tab.');
    }

    if (errors.length === 0) {
      const selectedSamplingList = sampling.filter(r => selectedSamplingIds.has(r.sampleLoginOid));
      // Hold samples are exempt from every mandatory check below — mirrors GoLive
      // FieldDataEntryViewController.cs:1133/1307
      // ("if (objSampling.SampleStatus == null || !objSampling.SampleStatus.Hold)").
      const nonHoldSamplingList = selectedSamplingList.filter(s => !s.isHold);

      // 3. For every selected non-Hold sample, ALL its monitoring parameters must be selected
      for (const s of nonHoldSamplingList) {
        const allMon = monitoring.filter(r => r.sampleLoginOid === s.sampleLoginOid);
        const selMon = allMon.filter(r => selectedMonitoringIds.has(r.sampleParameterOid));
        if (allMon.length > 0 && selMon.length < allMon.length) {
          errors.push(
            `Sample ${s.sampleId}: select all ${allMon.length} monitoring parameter(s) in ` +
            `Monitoring Records (${selMon.length} of ${allMon.length} selected).`
          );
        }
      }

      // 4/5. Mandatory fields are CONFIGURED PER MATRIX in Sample Field Configuration
      // (dbo.SamplingFieldConfiguration.IsMandatory), by FieldClass — 0 Sampling ·
      // 1 Station · 2 Monitoring. This mirrors legacy FieldDataEntryComplete_Execute
      // (FieldDataEntryViewController.cs ~1135-1272): it reads the sample's matrix config
      // and blocks on any empty IsMandatory field, Hold samples exempt — it does NOT
      // hard-code a fixed set. (Verified 2026-09-04 against AlpacaLims_GoLive: the live
      // config makes ONLY Collector + CollectDate (Sampling) and Result (Monitoring)
      // mandatory — NOT MonitoredBy/MonitoredDate, which the old hard-coded checks
      // wrongly required.) `fieldConfig` is the same SamplingFieldConfiguration set,
      // loaded per job in loadDetail.
      const isEmptyVal = (v: unknown) => v == null || (typeof v === 'string' && v.trim() === '');
      const fieldVal = (r: Record<string, unknown>, fieldId: string, map: Record<string, string>) =>
        // Fixed columns resolve FieldID → AI property via the tab's map; a dynamic
        // (config-only) field is stored on the row under its own FieldID.
        (map[fieldId] ? r[map[fieldId]] : r[fieldId]);
      const mandSampling   = fieldConfig.filter(f => f.isMandatory && f.fieldClass === 0);
      const mandStation    = fieldConfig.filter(f => f.isMandatory && f.fieldClass === 1);
      const mandMonitoring = fieldConfig.filter(f => f.isMandatory && f.fieldClass === 2);

      // 4a. Sampling-tab mandatory fields — each selected non-Hold sampling row.
      for (const s of nonHoldSamplingList) {
        for (const f of mandSampling) {
          if (isEmptyVal(fieldVal(s as unknown as Record<string, unknown>, f.fieldId, SAMPLING_FIELD_MAP)))
            errors.push(`Sample ${s.sampleId}: ${f.caption} is required.`);
        }
      }

      // 4b. Station-tab mandatory fields — the station row(s) of the selected samples.
      if (mandStation.length > 0) {
        const selStationNames = new Set(nonHoldSamplingList.map(s => s.stationName));
        for (const st of stations.filter(x => selStationNames.has(x.stationName))) {
          for (const f of mandStation) {
            if (isEmptyVal(fieldVal(st as unknown as Record<string, unknown>, f.fieldId, STATION_FIELD_MAP)))
              errors.push(`Station ${st.stationName}: ${f.caption} is required.`);
          }
        }
      }

      // 5. Monitoring-tab mandatory fields — each selected non-Hold monitoring row.
      const selectedMonList = monitoring.filter(r => selectedMonitoringIds.has(r.sampleParameterOid) && !r.isHold);
      for (const m of selectedMonList) {
        for (const f of mandMonitoring) {
          if (isEmptyVal(fieldVal(m as unknown as Record<string, unknown>, f.fieldId, MONITORING_FIELD_MAP)))
            errors.push(`${m.sampleId} – ${m.parameter}: ${f.caption} is required.`);
        }
      }
    }

    if (errors.length > 0) {
      notify.error(
        errors[0] +
        (errors.length > 1 ? ` (${errors.length - 1} more issue${errors.length > 2 ? 's' : ''})` : '')
      );
      return;
    }

    const ok = await confirmDlg({
      message: `Submit ${selectedSamplingIds.size} sample(s) for validation? ` +
               `This will move them to Pending Validation (FD-2-3).`,
      tone: 'default',
    });
    if (!ok) return;

    try {
      setActiveStep('submit');
      const selectedOids = [...selectedSamplingIds].map(String);
      await runLoading(
        api.post(`/api/v1/field-data-entry/${selectedJob.jobOid}/submit`, {
          selectedSampleOids: selectedOids,
        }),
        { blocking: true, label: 'Submitting…' }
      );
      notify.success('Field data submitted successfully.');
      closeDetail();
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })
        ?.response?.data?.error;
      const msg = typeof raw === 'string'
        ? raw
        : (raw as { message?: string } | undefined)?.message
          ?? 'Submit failed. Please try again.';
      notify.error(msg);
    }
  }, [
    selectedJob, isDirty, canSave,
    selectedSamplingIds, selectedMonitoringIds,
    sampling, monitoring, stations, fieldConfig,
    confirmDlg, notify, closeDetail,
  ]);

  // ── Delete (GoLive FieldDataDelete_Execute parity) ───────────────────────────
  // Selection-scoped "un-enter" reset of the ticked Sampling-tab samples: clears
  // the collection / monitoring / result values and returns them to Pending Data
  // Entry (SampleLogIn → PendingCompletion, field-test SampleParameter →
  // PendingEntry with Result cleared). NOT a row delete. Confirm-only (GoLive
  // prompts no reason); audited server-side as OperationType Deleted.
  const handleDelete = useCallback(async () => {
    if (!selectedJob) return;
    if (!canDelete) { notify.error('You do not have permission to delete field data.'); return; }
    const ids = [...selectedSamplingIds].map(String);
    if (ids.length === 0) {
      notify.error('Select at least one Sample ID in the Sampling Information tab to delete.');
      return;
    }

    const ok = await confirmDlg({
      title: 'Delete Field Data',
      message: `Reset entered field data for ${ids.length} selected sample${ids.length > 1 ? 's' : ''}? `
             + 'This clears the collection, monitoring and result values and returns '
             + 'the sample(s) to Pending Data Entry. This cannot be undone.',
      tone: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;

    try {
      await runLoading(
        api.post(`/api/v1/field-data-entry/${selectedJob.jobOid}/delete`, { selectedSampleOids: ids }),
        { blocking: true, label: 'Deleting…' });
      notify.success('Deleted successfully.');
      // After delete: DESELECT every tab (rows must uncheck — user request
      // 2026-08-31) and RELOAD so the cleared values + reset Sample Status show.
      setSelectedStationIds(new Set());
      setSelectedSamplingIds(new Set());
      setSelectedMonitoringIds(new Set());
      setIsDirty(false);
      await loadDetail(selectedJob);
    } catch {
      notify.error('Delete failed. Please try again.');
    }
  }, [selectedJob, canDelete, selectedSamplingIds, confirmDlg, notify, loadDetail]);

  // ── Sampling inline cell edit → local state only (no API call, no toast) ────
  // All data is persisted when the user clicks Save.
  const handleSamplingCellCommit = useCallback((
    row: SamplingRow, col: AppColumn<SamplingRow>, value: string
  ) => {
    // Editing a row auto-stamps EnteredBy / EnteredDate for that row (ALPACA parity).
    const stamp = currentUser ? { enteredBy: currentUser.name, enteredByOid: currentUser.id, enteredDate: new Date().toISOString() } : {};
    if (col.field === 'collectorName') {
      // Cleared (× on the combo) → clear the collector, don't try to resolve an
      // empty name as a user. Mirrors the collectedDate branch below; CollectedBy
      // stays required at SUBMIT (handleSubmit), so clearing mid-entry is allowed.
      // (Bug 2026-09-04: clearing showed a wrong "User not found." error.)
      if (!value || value.trim() === '') {
        setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
          ? { ...r, collectorOid: null, collectorName: null, ...stamp } : r));
        return;
      }
      const user = users.find(u => u.userName === value);
      if (!user) { notify.error('User not found.'); return; }
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, collectorOid: user.userOid, collectorName: user.userName, ...stamp } : r));
    } else if (col.field === 'collectedDate') {
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, collectedDate: value || null, ...stamp } : r));
    } else if (col.field === 'enteredBy') {
      // Manual EnteredBy pick — store the login name + its Employee Oid; NO auto-stamp.
      const emp = employees.find(e => e.name === value);
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, enteredBy: value, enteredByOid: emp?.oid ?? null } : r));
    } else if (col.field === 'enteredDate') {
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, enteredDate: value || null } : r));
    } else {
      setSampling(prev => prev.map(r => r.sampleLoginOid === row.sampleLoginOid
        ? { ...r, [col.field]: value, ...stamp } : r));
    }
    setIsDirty(true);
  }, [users, employees, notify, currentUser]);

  // ── Monitoring inline cell edit → local state only ──────────────────────────
  const handleMonitoringSave = useCallback((
    row: MonitoringRow, col: AppColumn<MonitoringRow>, value: string
  ) => {
    const stamp = currentUser ? { enteredBy: currentUser.name, enteredByOid: currentUser.id, enteredDate: new Date().toISOString() } : {};
    if (col.field === 'monitoredBy') {
      const mEmp = employees.find(e => e.name === value);
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid
          ? { ...r, monitoredBy: value, monitoredByOid: mEmp?.oid ?? null, ...stamp } : r));
    } else if (col.field === 'monitoredDate') {
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid ? { ...r, monitoredDate: value || null, ...stamp } : r));
    } else if (col.field === 'enteredBy') {
      const emp = employees.find(e => e.name === value);
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid ? { ...r, enteredBy: value, enteredByOid: emp?.oid ?? null } : r));
    } else if (col.field === 'enteredDate') {
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid ? { ...r, enteredDate: value || null } : r));
    } else {
      setMonitoring(prev => prev.map(r =>
        r.sampleParameterOid === row.sampleParameterOid ? { ...r, [col.field]: value, ...stamp } : r));
    }
    setIsDirty(true);
  }, [currentUser, users, employees]);

  // ── Station inline cell edit → local state only ─────────────────────────────
  const handleStationCellCommit = useCallback((
    row: StationRow, col: AppColumn<StationRow>, value: string
  ) => {
    setStations(prev => prev.map(r =>
      r.sampleLoginOid === row.sampleLoginOid ? { ...r, [col.field]: value } : r));
    setIsDirty(true);
  }, []);

  // ── Field-data cell context-menu actions (legacy parity, all 3 grids) ───────
  // Refresh = reload the job detail (reverts unsaved edits), same as the grid
  // toolbar's Refresh. Copy To All Cell = fill the right-clicked column's value
  // into every row (or every SELECTED row); routed through the grid's OWN commit
  // handler so FK-backed combos resolve their *Oid sibling and EnteredBy/Date are
  // stamped exactly as a manual edit — a blind field set would save stale oids.
  const handleCellRefresh = useCallback(() => {
    closeCellMenu();
    if (selectedJob) void loadDetail(selectedJob);
  }, [selectedJob, loadDetail, closeCellMenu]);

  const handleCellCopy = useCallback((scope: 'all' | 'selected') => {
    const { grid, rowId, field } = cellMenu;
    closeCellMenu();
    if (!field || rowId == null) return;
    const run = <T extends Record<string, unknown>>(
      rows: T[],
      idOf: (r: T) => string | number,
      selected: Set<string | number>,
      commit: (row: T, col: AppColumn<T>, value: string) => void,
    ) => {
      const src = rows.find(r => idOf(r) === rowId);
      const raw = src ? src[field] : undefined;
      if (raw === undefined || raw === null || raw === '') { notify.error('No value to copy.'); return; }
      if (scope === 'selected' && selected.size === 0) { notify.error('Select rows first.'); return; }
      const col = { field } as AppColumn<T>;
      const value = String(raw);
      let n = 0;
      rows.forEach(r => {
        if (idOf(r) === rowId) return;   // the source row already holds the value
        if (scope === 'all' || selected.has(idOf(r))) { commit(r, col, value); n++; }
      });
      notify.success(scope === 'all' ? 'Copied to all rows.' : `Copied to ${n} selected row(s).`);
    };
    if (grid === 'station') run(stations, r => r.sampleLoginOid, selectedStationIds, handleStationCellCommit);
    else if (grid === 'sampling') run(sampling, r => r.sampleLoginOid, selectedSamplingIds, handleSamplingCellCommit);
    else run(monitoring, r => r.sampleParameterOid, selectedMonitoringIds, handleMonitoringSave);
  }, [cellMenu, stations, sampling, monitoring, selectedStationIds, selectedSamplingIds, selectedMonitoringIds,
      handleStationCellCommit, handleSamplingCellCommit, handleMonitoringSave, notify, closeCellMenu]);

  // ── Sampling row selection → auto-populate EnteredBy / EnteredDate ─────────
  // Row selection is NOT a data edit — EnteredBy / EnteredDate are stamped only
  // when a cell in the row is actually edited (see handleSamplingCellCommit /
  // handleMonitoringSave), not on checkbox select (user request 2026-08-25).
  const handleSamplingSelectionChange = useCallback((ids: Set<string | number>) => {
    setSelectedSamplingIds(ids);
  }, []);

  // Monitoring Records ONLY (user request 2026-08-25): checking a row stamps
  // EnteredBy / EnteredDate; UNchecking it clears them again. (Sampling stays
  // edit-only.) Already-selected rows keep their original stamp.
  //
  // ALPACA parity (user request 2026-09-28): checking a row ALSO auto-fills
  // MonitoredBy = current login user and MonitoredDate = now — but ONLY when they
  // are still empty, so a value the user picked (combo / bulk apply) or one loaded
  // from the DB is never clobbered ("I want both": auto-fill AND manual override).
  // MonitoredBy/MonitoredDate are field data, so — unlike EnteredBy — they are NOT
  // cleared on uncheck (deselecting a row must not wipe entered monitoring data).
  const handleMonitoringSelectionChange = useCallback((ids: Set<string | number>) => {
    const newlyChecked   = [...ids].filter(id => !selectedMonitoringIds.has(id));
    const newlyUnchecked = [...selectedMonitoringIds].filter(id => !ids.has(id));
    setSelectedMonitoringIds(ids);
    if (newlyChecked.length || newlyUnchecked.length) {
      const now = new Date().toISOString();
      let stamped = false;
      setMonitoring(prev => prev.map(r => {
        if (currentUser && newlyChecked.includes(r.sampleParameterOid)) {
          stamped = true;
          return {
            ...r,
            enteredBy: currentUser.name, enteredByOid: currentUser.id, enteredDate: now,
            // Auto-fill only when empty (preserve a manual / pre-existing value).
            monitoredBy:    r.monitoredBy || currentUser.name,
            monitoredByOid: r.monitoredBy ? r.monitoredByOid : currentUser.id,
            monitoredDate:  r.monitoredDate || now,
          };
        }
        if (newlyUnchecked.includes(r.sampleParameterOid))
          return { ...r, enteredBy: null, enteredByOid: null, enteredDate: null };
        return r;
      }));
      // Auto-filling MonitoredBy/MonitoredDate is a real data change, so mark the
      // form dirty (the values persist on Save; Submit still requires a save first).
      if (stamped) setIsDirty(true);
    }
  }, [selectedMonitoringIds, currentUser]);

  // ── Sampling bulk-apply → local state only, persisted on Save ──────────────
  const handleApplyToSelected = useCallback(async () => {
    if (selectedSamplingIds.size === 0) { notify.error('Select at least one row.'); return; }
    if (!bulkCollectorOid && !bulkDate) { notify.error('Select CollectedBy or CollectedDate.'); return; }
    const ok = await confirmDlg({ message: `Apply to ${selectedSamplingIds.size} selected row(s)?`, tone: 'default' });
    if (!ok) return;
    const collectorName = users.find(u => u.userOid === bulkCollectorOid)?.userName ?? null;
    setSampling(prev => prev.map(r =>
      selectedSamplingIds.has(r.sampleLoginOid)
        ? { ...r,
            collectorOid:  bulkCollectorOid || r.collectorOid,
            collectorName: collectorName    ?? r.collectorName,
            collectedDate: bulkDate         || r.collectedDate }
        : r));
    setIsDirty(true);
    notify.success('Applied to selected rows.');
  }, [selectedSamplingIds, bulkCollectorOid, bulkDate, users, confirmDlg, notify]);


  // ── Monitoring bulk-apply → local state only, persisted on Save ─────────────
  const handleApplyMonitoringToSelected = useCallback(async () => {
    if (selectedMonitoringIds.size === 0) { notify.error('Select at least one row.'); return; }
    if (!bulkMonitoredByOid && !bulkMonitoredDate) { notify.error('Select Monitored By or Monitored Date.'); return; }
    const ok = await confirmDlg({ message: `Apply to ${selectedMonitoringIds.size} selected row(s)?`, tone: 'default' });
    if (!ok) return;
    const monitoredByName = employees.find(e => e.oid === bulkMonitoredByOid)?.name ?? null;
    setMonitoring(prev => prev.map(r =>
      selectedMonitoringIds.has(r.sampleParameterOid)
        ? { ...r,
            monitoredBy:    monitoredByName   ?? r.monitoredBy,
            monitoredByOid: bulkMonitoredByOid || r.monitoredByOid,
            monitoredDate:  bulkMonitoredDate  || r.monitoredDate }
        : r));
    setIsDirty(true);
    notify.success('Applied to selected rows.');
  }, [selectedMonitoringIds, bulkMonitoredByOid, bulkMonitoredDate, employees, monitoring, confirmDlg, notify]);

  // ── Island stepper steps ────────────────────────────────────────────────────
  // activeStep drives position: 'job' → 'stations' → 'sampling' → 'monitoring'
  //   → 'save' (set on Save success) → 'submit' (set when Submit fires)
  const stepOrder = ['job', 'stations', 'sampling', 'monitoring', 'save', 'submit'] as const;
  const islandSteps = useMemo((): IslandStep[] => {
    const idx = stepOrder.indexOf(activeStep as typeof stepOrder[number]);
    const stationsDone = selectedStationIds.size > 0;
    return [
      { id: 'job',        label: 'Select Job', state: !selectedJob ? 'active' : 'done' },
      // Stations is only 'done' once at least one station row is selected
      { id: 'stations',   label: 'Stations',   state: !selectedJob ? 'todo' : stationsDone ? 'done' : 'active' },
      { id: 'sampling',   label: 'Sampling',   state: !selectedJob ? 'todo' : idx > 2 ? 'done' : idx === 2 ? 'active' : 'todo' },
      { id: 'monitoring', label: 'Monitoring', state: !selectedJob ? 'todo' : idx > 3 ? 'done' : idx === 3 ? 'active' : 'todo' },
      { id: 'save',       label: 'Save',       state: !selectedJob ? 'optional' : idx > 4 ? 'done' : idx === 4 ? 'active' : 'optional' },
      { id: 'submit',     label: 'Submit',     state: !selectedJob ? 'todo' : idx >= 5 ? 'active' : 'todo' },
    ];
  }, [selectedJob, activeStep, selectedStationIds]);

  // ── Grid columns ────────────────────────────────────────────────────────────
  const stationColSet = useMemo(() => {
    const base: AppColumn<StationRow>[] = [
      { field: 'stationId',      header: 'JobID',            width: 120 },
      { field: 'stationName',    header: 'StationLocation',  width: 200 },
      { field: 'blended',        header: 'Blended',      width: 80,  align: 'center',
        text: r => r.blended ? 'true' : 'false',
        render: r => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <input type="checkbox" checked={r.blended} readOnly
              style={{ cursor: 'default', width: 14, height: 14 }} />
          </div>
        ) },
      { field: 'latitude',       header: 'Latitude',     width: 110, edit: { type: 'text', trigger: 'click' } },
      { field: 'longitude',      header: 'Longitude',    width: 110, edit: { type: 'text', trigger: 'click' } },
      { field: 'temperature',    header: 'Temp (°F)',     width: 90,  edit: { type: 'text', trigger: 'click' } },
      { field: 'humidity',       header: 'Humidity (%)', width: 100, edit: { type: 'text', trigger: 'click' } },
      { field: 'stationComment', header: 'Comment',      width: 200, edit: { type: 'text', trigger: 'click' } },
    ];
    const assembled = assembleTab(applyFieldConfig(base, fieldConfig, 1, STATION_FIELD_MAP), stationDyn, fieldConfig, 1, STATION_FIELD_MAP);
    // Legacy parity — Copy-To-All is FUNCTIONALLY editable-only. Although legacy's
    // GridView_FillContextMenuItems adds the item at the ROW level (so it visually
    // shows on every cell), FocusedCellChanging captures a copy source ONLY when the
    // column is in cpeditcolumnname (= columns with AllowEdit == true = !IsReadonly from
    // the field config); on a read-only column "Copy To All Cell" has no source and does
    // nothing (verified 2026-10-22, FieldDataEntryViewController.cs:432-521 — the
    // editable-gating is SHARED across all three Field Data Entry grids). So AI attaches
    // the menu ONLY to editable cells (the useful subset); a column you need to bulk-fill
    // (e.g. PlannedLatitude / PlannedLongitude) must be made editable in Sample Field
    // Configuration (IsReadonly off), exactly as legacy requires. Sampling / Monitoring
    // already gate the same way.
    const cols = assembled.cols.map((c): AppColumn<StationRow> =>
      c.edit
        ? { ...c, onCellContextMenu: (e, r) => openCellMenu('station', e.clientX, e.clientY, r.sampleLoginOid, c.field) }
        : c);
    return { ...assembled, cols };
  }, [fieldConfig, stationDyn, openCellMenu]);

  const samplingColSet = useMemo(() => {
    const base: AppColumn<SamplingRow>[] = [
      { field: 'stationName',   header: 'StationLocation', width: 180 },
      { field: 'sampleId',      header: 'SampleID',        width: 130 },
      // CollectedBy / CollectedDate are required — the header red * shows that and
      // Submit still enforces it (handleSubmit). No yellow required-empty CELL tint
      // (`requiredEmpty` dropped, user request 2026-09-04: "no needed").
      { field: 'collectorName',
        header: <>CollectedBy <span className="req-star">*</span></>,
        headerText: 'CollectedBy *',
        width: 160,
        edit: {
          type: 'combo',
          options: users.map(u => u.userName),
          trigger: 'click',
          // CollectedBy is required at SUBMIT but may be emptied mid-entry
          // (handleSamplingCellCommit clears it) — clearable enables the ✕ AND
          // Backspace-to-remove in the combo (user request 2026-09-25).
          clearable: true,
        } },
      { field: 'collectedDate',
        header: <>CollectedDate <span className="req-star">*</span></>,
        headerText: 'CollectedDate *',
        width: 150, type: 'datetime',
        render: r => <span>{fmtLocalDateTime(r.collectedDate)}</span>,
        edit: {
          type: 'datetime',
          trigger: 'click',
        } },
      { field: 'testSummary',   header: 'TestSummary',     width: 360 },
      { field: 'sampleComment', header: 'SampleComment',   width: 200 },
      // EnteredBy / EnteredDate ARE editable on the Sampling tab — the LIVE legacy app
      // renders an employee dropdown + date picker on these cells (owner report 2026-10-22),
      // even though the source Model.xafml marks them AllowEdit="False" (deployed-model
      // drift — the running app wins). Auto-stamped (current user / now) when a row is
      // ticked/edited, and hand-pickable via the combo. Excluded from the Copy To All
      // bulk-fill menu (SAMPLING_NO_MENU) — they are per-row who/when stamps.
      { field: 'enteredBy',     header: 'EnteredBy',       width: 130,
        edit: { type: 'combo', options: employees.map(e => e.name), trigger: 'click' } },
      { field: 'enteredDate',   header: 'EnteredDate',     width: 130, type: 'date',
        render: r => <span>{fmtLocalDate(r.enteredDate)}</span>,
        edit: { type: 'date', trigger: 'click' } },
    ];
    const assembled = assembleTab(applyFieldConfig(base, fieldConfig, 0, SAMPLING_FIELD_MAP), samplingDyn, fieldConfig, 0, SAMPLING_FIELD_MAP);
    // Legacy parity: the "Copy To All Cell" menu sits on EDITABLE cells only. EnteredBy /
    // EnteredDate ARE editable here (dropdown + date picker, live-legacy parity) but are
    // EXCLUDED from the bulk-fill menu — they are per-row who/when stamps, not bulk-filled.
    // The grid's suppressNativeContextMenu stops the browser menu leaking on them.
    const SAMPLING_NO_MENU = new Set(['enteredBy', 'enteredDate']);
    const cols = assembled.cols.map((c): AppColumn<SamplingRow> =>
      (c.edit && !SAMPLING_NO_MENU.has(c.field))
        ? { ...c, onCellContextMenu: (e, r) => openCellMenu('sampling', e.clientX, e.clientY, r.sampleLoginOid, c.field) }
        : c);
    return { ...assembled, cols };
  }, [users, employees, fieldConfig, samplingDyn, openCellMenu]);

  // sampleLoginOid -> station (site) name, from the loaded Station tab — the
  // Monitoring row carries sampleLoginOid but no station name (Routing the Trend
  // Icon, Part B, step 1).
  const stationNameByLogin = useMemo(
    () => new Map(stations.map((s) => [s.sampleLoginOid, s.stationName])),
    [stations],
  );
  const openTrend = useCallback((siteName: string, parameter: string) => {
    setTrendSite(siteName);
    setTrendParam(parameter);
    setTrendOpen(true);
  }, []);

  const monitoringColSet = useMemo(() => {
    const base: AppColumn<MonitoringRow>[] = [
      { field: 'sampleId',     header: 'SampleID',      width: 140, align: 'left' },
      { field: 'test',         header: 'Test',           width: 180, align: 'left' },
      { field: 'methodName',   header: 'MethodName',     width: 130, align: 'left' },
      { field: 'parameter',    header: 'Parameter',      width: 180, align: 'left' },
      { field: 'result',
        header: <>Result <span className="req-star">*</span></>,
        headerText: 'Result *',
        width: 110,
        // LEFT-aligned to match legacy ALPACA's Field Data Entry Monitoring Records
        // grid (user report 2026-10-20). A field result is stored as TEXT
        // (result: string | null — values like "BRL" / "<2" as well as numbers),
        // so left alignment is also the correct semantics, not number-column right.
        align: 'left',
        edit: { type: 'text', trigger: 'click' },
      },
      { field: 'unit',         header: 'Units',          width: 100, align: 'left' },
      { field: 'monitoredBy',  header: 'MonitoredBy',    width: 130, align: 'left',
        /* MonitoredBy is an Employee (SampleLogIn.MonitoredBy FK -> Employee), so
           the picker lists Employee DisplayNames — NOT the Collector roster; a
           Collector name never matched an Employee, so it saved NULL (2026-08-31). */
        edit: { type: 'combo', options: employees.map(e => e.name), trigger: 'click' } },
      { field: 'monitoredDate', header: 'MonitoredDate', width: 150, type: 'datetime', align: 'left',
        render: r => <span>{fmtLocalDateTime(r.monitoredDate)}</span>,
        edit: { type: 'datetime', trigger: 'click' } },
      { field: 'enteredBy',    header: 'EnteredBy',      width: 130, align: 'left',
        edit: { type: 'combo', options: employees.map(e => e.name), trigger: 'click' } },
      { field: 'enteredDate',  header: 'EnteredDate',    width: 130, type: 'date', align: 'left',
        render: r => <span>{fmtLocalDate(r.enteredDate)}</span>,
        edit: { type: 'date', trigger: 'click' } },
    ];
    // Config-mapped columns show only when selected + sorted by sortOrder; the
    // AI-only entry columns (monitoredBy, monitoredDate, enteredBy, enteredDate)
    // are always kept and go after (§ applyFieldConfig).
    const assembled = assembleTab(applyFieldConfig(base, fieldConfig, 2, MONITORING_FIELD_MAP), monitoringDyn, fieldConfig, 2, MONITORING_FIELD_MAP);
    // Trend column (Routing the Trend Icon, Part B) — one site-trend route per
    // row. Placed immediately AFTER the Units column (user request 2026-09-28);
    // falls back to last if Units isn't present.
    const trendCol: AppColumn<MonitoringRow> = {
      field: 'trend', header: 'Trend', headerText: 'Trend', width: 64,
      align: 'center', sortable: false, filterable: false, noExport: true,
      render: (r) => {
        const st = stationNameByLogin.get(r.sampleLoginOid) ?? '';
        return <TrendIconButton
          onClick={() => openTrend(st, r.parameter)}
          disabledReason={st ? undefined : 'No station for this row'} />;
      },
    };
    const cols = [...assembled.cols];
    const unitIdx = cols.findIndex(c => c.field === 'unit');
    if (unitIdx >= 0) cols.splice(unitIdx + 1, 0, trendCol);
    else cols.push(trendCol);
    // Legacy parity: right-click any editable cell → Refresh + Copy To All Cell.
    const withCtx = cols.map((c): AppColumn<MonitoringRow> =>
      c.edit
        ? { ...c, onCellContextMenu: (e, r) => openCellMenu('monitoring', e.clientX, e.clientY, r.sampleParameterOid, c.field) }
        : c);
    return { cols: withCtx, sticky: assembled.sticky };
  }, [users, employees, fieldConfig, monitoringDyn, openCellMenu, stationNameByLogin, openTrend]);

  // ── Render ──────────────────────────────────────────────────────────────────

  // Free-text filter over the list (Job ID / Client / Project / Matrix / Status).
  const filtered = useMemo(() => {
    if (!search.trim()) return listRows;
    const t = search.toLowerCase();
    // Null-safe: a field (projectId/projectName/…) can be null from the API — a bare
    // .toLowerCase() crashed the page to blank on a longer search (user report 2026-09-25).
    const has = (v: string | null | undefined) => (v ?? '').toLowerCase().includes(t);
    return listRows.filter(x =>
      has(x.jobId) || has(x.client) || has(x.projectId) ||
      has(x.projectName) || has(x.sampleMatrix) || has(x.status) || has(x.statusId));
  }, [listRows, search]);

  // ── List grid columns (JobID · Client · ProjectID · ProjectName ·
  //    SampleMatrix · Status · StatusID) — ALPACA Field Data Entry parity. ──
  const listCols: AppColumn<FieldDataEntryRow>[] = [
    { field: 'jobId',        header: 'JobID',         width: 130 },
    { field: 'client',       header: 'Client',        width: 220 },
    { field: 'projectId',    header: 'ProjectID',     width: 130 },
    { field: 'projectName',  header: 'ProjectName',   width: 230 },
    { field: 'sampleMatrix', header: 'SampleMatrix',  width: 140 },
    { field: 'status',       header: 'Status',        width: 210,
      render: r => <span className={`bl-pill ${statusPillVariant(r.statusId)}`}>{r.status}</span> },
    { field: 'statusId',     header: 'StatusID',      width: 100 },
  ];

  // The single ticked job (Load target).
  const loadedJob = selectedIds.size === 1
    ? listRows.find(r => selectedIds.has(r.jobOid)) ?? null
    : null;

  // Field Data Entry keys (CLAUDE.md standing rule 14, §18.6, ADR-0013):
  //  · LIST view   — Alt+L Load (opens the ticked job).
  //  · DETAIL view — Ctrl+S Save, Ctrl+Enter Submit (mirror the toolbar buttons).
  useHotkeys(view === 'list'
    ? [{ combo: 'alt+l', label: 'Load', group: 'Field Data Entry', order: 1,
        run: () => { if (loadedJob) openDetail(loadedJob); }, disabled: !loadedJob }]
    : [
      { combo: 'mod+s', label: 'Save', group: 'Field Data Entry', order: 1,
        run: () => { void handleSave(); }, disabled: !canSave },
      { combo: 'mod+enter', label: 'Submit', group: 'Field Data Entry', order: 2,
        run: () => { void handleSubmit(); }, disabled: !canSave },
      { combo: 'esc', label: 'Back to list', group: 'Field Data Entry', order: 3,
        allowInInput: false, run: () => { closeDetail(); } },
    ]);

  // ═══════════════════════════════════════════════════════════════════
  // LIST VIEW — jobs pending field-data capture (front view; standing rule 10)
  // ═══════════════════════════════════════════════════════════════════
  if (view === 'list') {
    return (
      <div className="bl-page">
        <TopbarStepper steps={islandSteps} />

        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Sampling Management</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Field Data Entry</span>
            </div>

            <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormTitle pageId="fielddata-entry" auditFormName="Field Data Entry"
                loadMs={listLoadMs} loading={listLoading} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="ca-btn ca-btn-solid"
                  disabled={!loadedJob}
                  onClick={() => { if (loadedJob) openDetail(loadedJob); }}
                  title={!loadedJob ? 'Tick a Job ID to load' : `Load ${loadedJob.jobId} (${formatCombo('alt+l')})`}
                >
                  <span aria-hidden="true">⬇️</span>&nbsp;Load
                </button>
              </div>
            </div>

            <StepHint tone="guidance">
              <b>Step 1 · Select Job.</b> Tick a <b>Job ID</b> and click <b>Load</b> (the legacy “Retrieve”), or <b>double-click a row</b>, to open its Station, Sampling and Monitoring data.
            </StepHint>
          </div>
        </div>

        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              onRefresh={() => void loadList()}
              refreshing={listLoading}
              exportData={{ filename: 'field-data-entry', ...gridExportMatrix(listVisible, listCols) }}
            />
          </div>

          {/* Search — shared .list-search-row on the grey page, below the toolbar,
              never inside the toolbar/card (§18.6, Sample Transfer parity). */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>

          {search && filtered.length !== listRows.length && (
            <div style={{ marginBottom: 6 }}>
              <span className="search-match-count">{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
            </div>
          )}

          <div className="bl-card bl-card-flex">
            {listError ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {listError}
                <button onClick={() => void loadList()}
                  style={{ marginLeft: 10, color: 'var(--brand-accent,#e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Retry
                </button>
              </div>
            ) : (
              <AppDataGrid<FieldDataEntryRow>
                maximizable
                rows={filtered}
                columns={listCols}
                getRowId={r => r.jobOid}
                loading={listLoading}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={handleSelectionChange}
                onVisibleRowsChange={setListVisible}
                /* No row double-click / Enter open — the ONLY way to open a job is
                   to tick its checkbox and click Load (or Alt+L), matching Sample
                   Transfer (user request 2026-10-23). */
                emptyText="Nothing pending field data entry."
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // DETAIL VIEW
  if (selectedJob) {
    // Dynamic step hint per tab — matches prototype Step 2/3/4
    const detailHint = activeTab === 'stations'
      ? <><b>Step 2 · Stations.</b> <b>Select all rows</b> (header checkbox or Select All) to complete this step — the stepper turns green per tab. Click a cell to edit; GPS / weather are optional.</>
      : activeTab === 'sampling'
      ? <><b>Step 3 · Sampling.</b> <b>Select all rows</b>, then fill <b>CollectedBy</b> and <b>CollectedDate</b> — click a cell to edit.</>
      : <><b>Step 4 · Monitoring.</b> <b>Select all rows</b> and fill <b>every Result</b> — empty results are not allowed before submission (yellow cells).</>;

    const samplingCols = samplingColSet.cols;
    const monitoringCols = monitoringColSet.cols;

    return (
      <>
      <div className="bl-page">
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span>
              <i className="ti ti-chevron-right" />
              <span>Sampling Management</span>
              <i className="ti ti-chevron-right" />
              <span
                style={{ color: 'var(--brand-accent,#e07b39)', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={closeDetail}
              >Field Data Entry</span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-active">{selectedJob.jobId}</span>
            </div>

            {/* Title row — just the form title (Save/Submit/Delete moved to the
                action toolbar below, REE-style). */}
            <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormTitle pageId="fielddata-entry" auditFormName="Field Data Entry"
                loadMs={detailLoadMs} loading={detailLoading}
                title={`Field Data Entry — ${selectedJob.jobId}`} />
            </div>

            {/* Island stepper — portalled into AppShell topbar */}
            <TopbarStepper steps={islandSteps} />

            {/* Shared Project Notes popup — job-scoped via this module's route.
                businessObject is ALPACA's SourceName for this form, stamped on a
                new note's NoteSource. Project Notes are per-job, so they open only
                when a SINGLE job is loaded (notesJobOid). */}
            {notesOpen && notesUrl && notesJobOid && (
              <NotesListPopup
                batchId={notesJobOid}
                batchTest="Field Data Entry"
                batchLabel={notesJobLabel}
                businessObject="Field Data Entry"
                baseUrl={notesUrl}
                onClose={() => setNotesOpen(false)}
              />
            )}

            {/* Trend popup — the Monitoring "Trend" column + toolbar Trending button */}
            <TrendChartPopup
              open={trendOpen}
              onClose={() => setTrendOpen(false)}
              siteTrend={{
                siteName: trendSite,
                parameter: trendParam,
                siteOptions: trendSite ? [] : Array.from(new Set(stations.map((s) => s.stationName).filter(Boolean))),
                job: { jobId: selectedJob.jobId, client: selectedJob.client,
                       projectId: selectedJob.projectId, projectName: selectedJob.projectName },
              }}
            />

            {/* Dynamic step hint — changes per tab */}
            <StepHint tone="guidance">{detailHint}</StepHint>
          </div>
        </div>

        <div className="bl-grid-area">
          {/* General Information card — the selected job's header. */}
          <div className="bl-card" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderBottom: '1px solid var(--line, #e8eaf0)', background: 'linear-gradient(180deg,#fff,#f7f8fb)' }}>
              <span style={{ width: 4, height: 16, borderRadius: 3, background: 'var(--brand-accent, #e07b39)', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontWeight: 700, fontSize: 12.5, color: '#15203a' }}>General Information</span>
              <span className={`bl-pill ${statusPillVariant(selectedJob.statusId)}`} style={{ marginLeft: 4 }}>{selectedJob.status}</span>
            </div>
            <div style={{ padding: '7px 12px 8px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '3px 18px' }}>
              {([
                ['JobID',       selectedJob.jobId],
                ['Client',      selectedJob.client],
                ['SampleMatrix',selectedJob.sampleMatrix],
                ['ProjectID',   selectedJob.projectId],
                ['ProjectName', selectedJob.projectName],
                ['StatusID',    selectedJob.statusId],
              ] as [string, string][]).map(([lbl, val]) => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ width: 96, flexShrink: 0, fontSize: 11, color: '#4a5260' }}>{lbl}</label>
                  <div style={{ flex: 1, minWidth: 0, padding: '2px 7px', border: '1px solid #e3e6ec', borderRadius: 4, fontSize: 11.5, background: '#f7f8fa', color: '#3a414e', height: 23, display: 'flex', alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>{val || '—'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Action toolbar — sits ABOVE the tabs (REE
              layout): Project Notes + Refresh on the left, Save / Submit / Delete
              flush right. */}
          <div style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 0 }}>
            <AppToolbar
              /* Project Notes sits FIRST, before Refresh (user request 2026-08-03).
                 Supplied through the shared toolbar's `left` slot — the sanctioned
                 place for form-specific actions (§18.6) — so it inherits the standard
                 .ca-btn styling and the canonical order is preserved. */
              left={
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {/* Project Notes — ALWAYS visible (REE parity). Notes are per-job,
                      so it enables only once the load resolves to a SINGLE job,
                      scoped to that job; disabled otherwise (before Load / multi-job). */}
                  <button className="ca-btn" onClick={() => setNotesOpen(true)}
                    disabled={!notesJobOid}
                    title={notesJobOid
                      ? `Project Notes for job ${notesJobLabel}`
                      : 'Load a single Job ID to view its project notes'}>
                    <span style={{ marginRight: 3 }}>🔔</span> Project Notes ({notesCount})
                  </button>
                  {/* Trending — only on the Monitoring Records tab (per-row Trend
                      column also opens it). */}
                  {activeTab === 'monitoring' && (
                    <button className="ca-btn" onClick={() => openTrend('', '')}
                      title="Concentration trend — pick a site and parameter">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#e8670a" strokeWidth="2.4"
                        strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: '-2px' }}>
                        <path d="M3 17l5-5 4 3 7-8" /><path d="M15 4h5v5" /></svg>
                      Trending
                    </button>
                  )}
                </div>
              }
              onRefresh={() => void loadDetail(selectedJob)}
              refreshing={detailLoading}
            />
            <span style={{ marginLeft: 8, fontSize: 11,
              fontWeight: isDirty ? 700 : 400,
              color: isDirty ? '#b45309' : '#9ca3af' }}>
              ● {isDirty ? 'Unsaved changes' : 'Saved'}
            </span>
            {showColsLoading && (
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                  marginLeft: 10, fontSize: 11, color: '#6b7280' }}
                title="The matrix's configured columns are still loading"
              >
                <LoaderSymbol size={13} /> Loading columns…
              </span>
            )}
            {/* Back / Save / Submit / Delete — flush right on the action toolbar
                (REE-style). Permissions use the finer canSave / canDelete (nav
                Write AND MAC). */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              {/* Back — return to the Field Data Entry list view (user request
                  2026-09-28); mirrors the breadcrumb, bound to Esc (rule 14). */}
              <button className="ca-btn" onClick={closeDetail}
                title={`Back to list (${formatCombo('esc')})`}>
                <span style={{ marginRight: 3 }}>←</span> Back
              </button>
              <button className="ca-btn ca-btn-solid" onClick={handleSave}
                disabled={!canSave}
                title={(!canSave ? 'You do not have permission to save field data' : 'Save field data') + ` (${formatCombo('mod+s')})`}>
                <span style={{marginRight:3}}>💾</span> Save
              </button>
              <button className="ca-btn" onClick={handleSubmit}
                disabled={!canSave}
                title={(!canSave ? 'You do not have permission to submit field data' : 'Submit field data') + ` (${formatCombo('mod+enter')})`}>
                <span style={{marginRight:3}}>⬆️</span> Submit
              </button>
              {/* Delete is gated on the EDITABLE state, not on a pre-selection
                  (owner decision 2026-10-20). Legacy FieldDataDelete is a plain
                  SimpleAction — enabled in EDIT mode, disabled in VIEW mode — and on
                  click with nothing ticked it shows "Select atleast one sample to
                  delete". So here it is disabled only while the form is read-only
                  (!canWrite) or the user lacks delete permission; when editable it is
                  enabled, and handleDelete surfaces the same "select a sample" message
                  for an empty click. */}
              <button className="ca-btn ca-btn-danger" onClick={handleDelete}
                disabled={!canDelete}
                title={!canDelete
                  ? 'Delete is available only while the form is editable and you have delete permission'
                  : undefined}>
                <span className="tb-i tb-i-red">🗑</span> Delete
              </button>
            </div>
          </div>

          {/* Tab strip — Station Information / Sampling Information / Monitoring Records */}
          <div className="sa-filter-row" style={{ marginBottom: 8 }}>
            {(['stations', 'sampling', 'monitoring'] as TabKey[]).map(tab => (
              <button
                key={tab}
                className={`sa-filter-btn${activeTab === tab ? ' active' : ''}`}
                onClick={() => {
                  setActiveTab(tab);
                  // Advance stepper to the tab's step only if not already past it
                  const tabStep = tab === 'stations' ? 'stations' : tab === 'sampling' ? 'sampling' : 'monitoring';
                  setActiveStep(prev => {
                    const cur = stepOrder.indexOf(prev as typeof stepOrder[number]);
                    const next = stepOrder.indexOf(tabStep);
                    return next > cur ? tabStep : prev;
                  });
                }}
              >
                {tab === 'stations'
                  ? 'Station Information'
                  : tab === 'sampling'
                    ? (<>Sampling Information{selectedSamplingIds.size === sampling.length && sampling.length > 0 ? <span style={{color:'#22c55e',marginLeft:4,fontWeight:700}}>✓</span> : <span className="req-star">*</span>}</>)
                    : (<>Monitoring Records<span className="req-star">*</span></>)}
              </button>
            ))}
          </div>

          <div className="bl-card bl-card-flex">
            {activeTab === 'stations' && (<>
              {/* Station toolbar — Select All, Refresh, Export to, hint caption */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderBottom: '1px solid var(--line,#e8eaf0)', flexWrap: 'wrap', background: '#fafbfc' }}>
                <button className="ca-btn" style={{ fontSize: 11 }}
                  onClick={() => setSelectedStationIds(
                    selectedStationIds.size === stations.length
                      ? new Set()
                      : new Set(stations.map(r => r.sampleLoginOid)))}>
                  {selectedStationIds.size === stations.length && stations.length > 0
                    ? '☑ Deselect All' : '☐ Select All'}
                </button>
                <button className="ca-btn" onClick={() => void loadDetail(selectedJob!)}>
                  <span className="tb-i tb-i-green">🔄</span> Refresh
                </button>
                <ExportMenu filename="station-information" {...gridExportMatrix(stationVisible, stationColSet.cols)} />
                <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 8 }}>
                  Click a cell to edit in place. GPS / weather are optional; Blended marks a blended-run station.
                </span>
              </div>
              <AppDataGrid<StationRow>
                maximizable
                suppressNativeContextMenu
                rows={stations}
                columns={stationColSet.cols}
                stickyColumns={stationColSet.sticky}
                getRowId={r => r.sampleLoginOid}
                onVisibleRowsChange={setStationVisible}
                loading={detailLoading}
                emptyText="No station records found"
                selectable
                selectedIds={selectedStationIds}
                onSelectionChange={ids => setSelectedStationIds(ids)}
                onCellCommit={canSave ? handleStationCellCommit : undefined}
              />
            </>)}
            {activeTab === 'sampling' && (<>
              {/* Bulk-apply toolbar — matches legacy layout */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderBottom: '1px solid var(--line,#e8eaf0)', flexWrap: 'wrap', background: '#fafbfc' }}>
                <button className="ca-btn" style={{ fontSize: 11 }}
                  onClick={() => handleSamplingSelectionChange(
                    selectedSamplingIds.size === sampling.length
                      ? new Set()
                      : new Set(sampling.map(r => r.sampleLoginOid)))}>
                  {selectedSamplingIds.size === sampling.length && sampling.length > 0
                    ? '☑ Deselect All' : '☐ Select All'}
                </button>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a5260' }}>Collected By<span className="req-star">*</span>:</label>
                <select value={bulkCollectorOid} onChange={e => setBulkCollectorOid(e.target.value)}
                  style={{ height: 26, fontSize: 11, border: '1px solid #d0d4de', borderRadius: 4,
                    padding: '0 4px', minWidth: 160 }}>
                  <option value="">— select —</option>
                  {users.map(u => (
                    <option key={u.userOid} value={u.userOid}>{u.userName}</option>
                  ))}
                </select>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a5260' }}>Collected Date<span className="req-star">*</span>:</label>
                <input type="datetime-local" value={bulkDate} onChange={e => setBulkDate(e.target.value)}
                  className={bulkDate ? undefined : 'date-empty'}
                  style={{ height: 26, fontSize: 11, border: '1px solid #d0d4de', borderRadius: 4,
                    padding: '0 6px' }} />
                <button className="ca-btn"
                  disabled={bulkApplying || selectedSamplingIds.size === 0 || (!bulkCollectorOid && !bulkDate)}
                  onClick={() => void handleApplyToSelected()}>
                  {bulkApplying ? '…' : '✓ Apply to Selected'}
                </button>
                <button className="ca-btn" onClick={() => void loadDetail(selectedJob!)}>
                  <span className="tb-i tb-i-green">🔄</span> Refresh
                </button>
                <ExportMenu filename="sampling-information" {...gridExportMatrix(samplingVisible, samplingCols)} />
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
                  {selectedSamplingIds.size} of {sampling.length} selected
                </span>
              </div>
              <AppDataGrid<SamplingRow>
                maximizable
                suppressNativeContextMenu
                rows={sampling}
                columns={samplingCols}
                stickyColumns={samplingColSet.sticky}
                getRowId={r => r.sampleLoginOid}
                onVisibleRowsChange={setSamplingVisible}
                loading={detailLoading}
                emptyText="No sampling records found"
                selectable
                selectedIds={selectedSamplingIds}
                onSelectionChange={handleSamplingSelectionChange}
                onCellCommit={canSave ? (row, col, val) => void handleSamplingCellCommit(row, col, val) : undefined}
              />
            </>)}
            {activeTab === 'monitoring' && (<>
              {/* Monitoring bulk-apply toolbar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderBottom: '1px solid var(--line,#e8eaf0)', flexWrap: 'wrap', background: '#fafbfc' }}>
                <button className="ca-btn" style={{ fontSize: 11 }}
                  onClick={() => handleMonitoringSelectionChange(
                    selectedMonitoringIds.size === monitoring.length
                      ? new Set()
                      : new Set(monitoring.map(r => r.sampleParameterOid)))}>
                  {selectedMonitoringIds.size === monitoring.length && monitoring.length > 0
                    ? '☑ Deselect All' : '☐ Select All'}
                </button>
                <span style={{ width: 1, height: 20, background: '#dcdcdc', margin: '0 2px' }} />
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a5260' }}>Monitored By</label>
                <select value={bulkMonitoredByOid} onChange={e => setBulkMonitoredByOid(e.target.value)}
                  style={{ height: 26, fontSize: 11, border: '1px solid #d0d4de', borderRadius: 4, padding: '0 4px', minWidth: 140 }}>
                  <option value="">— select —</option>
                  {employees.map(e => (
                    <option key={e.oid} value={e.oid}>{e.name}</option>
                  ))}
                </select>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#4a5260' }}>Monitored Date</label>
                <input type="datetime-local" value={bulkMonitoredDate} onChange={e => setBulkMonitoredDate(e.target.value)}
                  className={bulkMonitoredDate ? undefined : 'date-empty'}
                  style={{ height: 26, fontSize: 11, border: '1px solid #d0d4de', borderRadius: 4, padding: '0 6px' }} />
                <button className="ca-btn"
                  disabled={bulkMonitoringApplying || selectedMonitoringIds.size === 0 || (!bulkMonitoredByOid && !bulkMonitoredDate)}
                  onClick={() => void handleApplyMonitoringToSelected()}>
                  {bulkMonitoringApplying ? '…' : '✓ Apply to Selected'}
                </button>
                <span style={{ width: 1, height: 20, background: '#dcdcdc', margin: '0 2px' }} />
                <button className="ca-btn" onClick={() => void loadDetail(selectedJob!)}>
                  <span className="tb-i tb-i-green">🔄</span> Refresh
                </button>
                <ExportMenu filename="monitoring-records" {...gridExportMatrix(monitoringVisible, monitoringCols)} />
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
                  {selectedMonitoringIds.size} of {monitoring.length} selected
                </span>
              </div>
              <AppDataGrid<MonitoringRow>
                maximizable
                suppressNativeContextMenu
                rows={monitoring}
                columns={monitoringCols}
                stickyColumns={monitoringColSet.sticky}
                getRowId={r => r.sampleParameterOid}
                onVisibleRowsChange={setMonitoringVisible}
                loading={detailLoading}
                emptyText="No monitoring records found"
                selectable
                selectedIds={selectedMonitoringIds}
                onSelectionChange={handleMonitoringSelectionChange}
                onCellCommit={canSave ? (row, col, val) => void handleMonitoringSave(row, col, val) : undefined}
              />
            </>)}
          </div>
        </div>
      </div>

      {/* ── Field-data cell right-click context menu — ONE menu for Station /
          Sampling / Monitoring, legacy FieldDataEntryViewController parity
          (Refresh + Copy To All Cell; Copy to Selected Rows is the AI extension
          that already existed on Monitoring, now on every field-data grid). ── */}
      {cellMenu.visible && (
        <>
          {/* Backdrop — click anywhere outside closes the menu */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onClick={closeCellMenu}
            onContextMenu={e => { e.preventDefault(); closeCellMenu(); }}
          />
          <div style={{
            position: 'fixed', left: cellMenu.x, top: cellMenu.y, zIndex: 9999,
            background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
            boxShadow: '0 4px 16px rgba(0,0,0,0.13)', minWidth: 190, overflow: 'hidden',
            fontSize: 13, fontFamily: 'inherit',
          }}>
            <button
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', color: '#1f2430' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              onClick={handleCellRefresh}
            >
              <i className="ti ti-refresh" style={{ fontSize: 14, color: '#6b7280' }} />
              Refresh
            </button>
            <div style={{ height: 1, background: '#e5e7eb', margin: '0 8px' }} />
            <button
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', color: '#1f2430' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              onClick={() => handleCellCopy('all')}
            >
              <i className="ti ti-copy" style={{ fontSize: 14, color: '#6b7280' }} />
              Copy to All Cells
            </button>
            <div style={{ height: 1, background: '#e5e7eb', margin: '0 8px' }} />
            <button
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', color: '#1f2430' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              onClick={() => handleCellCopy('selected')}
            >
              <i className="ti ti-checkbox" style={{ fontSize: 14, color: '#6b7280' }} />
              Copy to Selected Rows
            </button>
          </div>
        </>
      )}
      </>
    );
  }

  // selectedJob is initialised to (and never leaves) the combined sentinel, so
  // the detail branch above always renders — there is no list view. This return
  // is unreachable and exists only to satisfy the compiler.
  return null;
}
